import { useEffect, useState } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  CircularProgress,
  FormHelperText,
} from "@mui/material";

import { Col } from "react-bootstrap";
import { apiRequest } from "../services/apiService";

export default function LocationSelect({
  values,
  setFieldValue,
  errors,
  touched,
}) {
  const [voivodeshipList, setVoivodeshipList] = useState([]);
  const [cities, setCities] = useState([]);

  const [loadingVoivodeships, setLoadingVoivodeships] = useState(false);

  const [loadingCities, setLoadingCities] = useState(false);

  /*
   * ============================================================
   * POBIERANIE WOJEWÓDZTW
   * ============================================================
   */

  useEffect(() => {
    const fetchVoivodeships = async () => {
      setLoadingVoivodeships(true);

      try {
        const response = await apiRequest("voivodeship");

        setVoivodeshipList(response || []);
      } catch (error) {
        console.error("Błąd pobierania województw:", error);

        setVoivodeshipList([]);
      } finally {
        setLoadingVoivodeships(false);
      }
    };

    fetchVoivodeships();
  }, []);

  /*
   * ============================================================
   * POBIERANIE MIAST
   * ============================================================
   */

  const fetchCities = async (voivodeshipId) => {
    if (!voivodeshipId) {
      setCities([]);
      return;
    }

    setLoadingCities(true);

    try {
      const response = await apiRequest(`cities/${voivodeshipId}`);

      setCities(response || []);
    } catch (error) {
      console.error("Błąd pobierania miast:", error);

      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  };

  /*
   * ============================================================
   * STYLE
   * ============================================================
   */

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      backgroundColor: "#fff",
    },
  };

  /*
   * ============================================================
   * RENDER
   * ============================================================
   */

  return (
    <>
      {/* =======================================================
          WOJEWÓDZTWO
      ======================================================= */}

      <Col xs={12} md={6}>
        <FormControl
          fullWidth
          margin="normal"
          error={touched.voivodeship && Boolean(errors.voivodeship)}
        >
          <InputLabel id="voivodeship-label">Województwo</InputLabel>

          <Select
            labelId="voivodeship-label"
            name="voivodeship"
            label="Województwo"
            value={values.voivodeship || ""}
            disabled={loadingVoivodeships}
            onChange={(e) => {
              const voivodeshipId = e.target.value;

              /*
               * Zapisujemy województwo
               */

              setFieldValue("voivodeship", voivodeshipId);

              /*
               * Po zmianie województwa
               * kasujemy poprzednio
               * wybrane miasto.
               */

              setFieldValue("city", null);

              /*
               * Czyścimy poprzednią listę,
               * żeby użytkownik nie zobaczył
               * miast z poprzedniego województwa.
               */

              setCities([]);

              /*
               * Pobieramy nową listę miast.
               */

              fetchCities(voivodeshipId);
            }}
            sx={{
              borderRadius: 2,
              backgroundColor: "#fff",
            }}
          >
            {loadingVoivodeships && (
              <MenuItem disabled>
                <CircularProgress size={18} sx={{ mr: 1 }} />
                Ładowanie województw...
              </MenuItem>
            )}

            {!loadingVoivodeships &&
              voivodeshipList.map((voivodeship) => (
                <MenuItem key={voivodeship.id} value={voivodeship.id}>
                  {voivodeship.nazwa}
                </MenuItem>
              ))}
          </Select>

          {touched.voivodeship && errors.voivodeship && (
            <FormHelperText>{errors.voivodeship}</FormHelperText>
          )}
        </FormControl>
      </Col>

      {/* =======================================================
          MIASTO
      ======================================================= */}

      <Col xs={12} md={6}>
        <Autocomplete
          options={cities}
          value={values.city || null}
          disabled={!values.voivodeship}
          loading={loadingCities}
          /*
           * Tekst widoczny na liście.
           */

          getOptionLabel={(option) => option?.nazwa || ""}
          /*
           * Ważne przy obiektach pobieranych
           * ponownie z API.
           */

          isOptionEqualToValue={(option, value) => option.id === value?.id}
          /*
           * Po wyborze zapisujemy cały
           * obiekt miasta w Formiku.
           */

          onChange={(_, value) => {
            setFieldValue("city", value);
          }}
          /*
           * Tekst wyświetlany,
           * gdy nic nie znaleziono.
           */

          noOptionsText={
            values.voivodeship
              ? "Nie znaleziono miejscowości"
              : "Najpierw wybierz województwo"
          }
          loadingText="Pobieranie miejscowości..."
          renderInput={(params) => (
            <TextField
              {...params}
              name="city"
              label="Miasto / miejscowość"
              placeholder={
                values.voivodeship
                  ? "Zacznij wpisywać nazwę..."
                  : "Najpierw wybierz województwo"
              }
              margin="normal"
              error={touched.city && Boolean(errors.city)}
              helperText={
                touched.city
                  ? errors.city
                  : values.voivodeship
                    ? "Wpisz kilka pierwszych liter miejscowości."
                    : "Wybierz województwo, aby wybrać miejscowość."
              }
              sx={fieldSx}
              InputProps={{
                ...params.InputProps,

                endAdornment: (
                  <>
                    {loadingCities && (
                      <CircularProgress
                        size={20}
                        sx={{
                          mr: 1,
                        }}
                      />
                    )}

                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          sx={fieldSx}
        />
      </Col>
    </>
  );
}
